import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus, FaRedoAlt, FaFilter, FaFileExcel } from 'react-icons/fa';
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Form,
  Badge,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { v4 } from 'uuid';
import moment from 'moment';

import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import DataTable from '../../components/DataTable';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import { useNotification } from '../../components/Notification/NotificationContext';
import ErrorProcessor from '../../components/ErrorProcessor';
import ToXLSX from '../../components/Exports/ToXlsx';
import CardHeaderMore from '../../components/Cards/CardHeaderMore';

export default function Customers() {
  const routeName = '/customers';
  const dataName = 'Cliente';
  const titleName = 'Clientes';
  const navigate = useNavigate();
  const [apiErrors, setApiErrors] = useState({});
  const notification = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const userData = useSelector((state) => state.auth.user);
  const [dataExport, setDataExport] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [initialPageSize] = useState(10); // Default page size for initial load and DataTable
  const [currentPageSize, setCurrentPageSize] = useState(initialPageSize);

  const filterOptionsCompany = useRef([]);
  const [filterOptionsDiscount, setFilterOptionsDiscount] = useState([]);
  const filterOptionsStatusCustomer = useRef([]);
  const filterOptionsStatusContract = useRef([]);
  const [filterApplied, setFilterApplied] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterLastname, setFilterLastname] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterDocId, setFilterDocId] = useState('');
  const [filterDiscount, setFilterDiscount] = useState('');
  const [filterStatusCustomer, setFilterStatusCustomer] = useState(1);
  const [filterStatusContract, setFilterStatusContract] = useState('');
  const [filterPartner, setFilterPartner] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);

  const baseTableColumns = useMemo(
    () => [
      { Header: 'Nome completo', accessor: 'name', Cell: ({ row }) => `${row.original.name} ${row.original.lastname}` },
      { Header: 'CPF / CNPJ', accessor: 'cpf_cnpj' },
      { Header: 'Empresa', accessor: 'company.name' },
      { Header: 'Situação contratual', accessor: 'status_contract' },
      { Header: 'Situação cliente', accessor: 'status_customer' },
    ],
    []
  );

  const exportDataConstruct = useCallback((customersToExport) => {
    const formatedExportData = [];
    if (Array.isArray(customersToExport)) {
      customersToExport.forEach((customerObj) => {
        const companyName = customerObj.Company && customerObj.Company.name ? customerObj.Company.name : '';
        const address = customerObj.CustomerAddresses && customerObj.CustomerAddresses[0] ? customerObj.CustomerAddresses[0] : {};
        const payment = customerObj.CustomerPayments && customerObj.CustomerPayments[0] ? customerObj.CustomerPayments[0] : {};
        const contract = customerObj.CustomerContracts && customerObj.CustomerContracts[0] ? customerObj.CustomerContracts[0] : {};

        const customerFormat = {
          Data_cadastro: customerObj.created_at ? moment.utc(customerObj.created_at).format('DD/MM/YYYY HH:mm:ss') : '',
          Data_atualização: customerObj.updated_at ? moment.utc(customerObj.updated_at).format('DD/MM/YYYY HH:mm:ss') : '',
          Nome: customerObj.name || '',
          Sobrenome: customerObj.lastname || '',
          CPF_CNPJ: customerObj.cpf_cnpj || '',
          Empresa: companyName,
          Tipo_de_pessoa: customerObj.type_person || '',
          Data_nascimento: customerObj.dob ? moment.utc(customerObj.dob).format('DD/MM/YYYY') : '',
          Idade: customerObj.age || '',
          Email: customerObj.email || '',
          Telefone_residêncial: customerObj.landline_phone || '',
          Telefone_comercial: customerObj.business_phone || '',
          Celular: customerObj.mobile_phone || '',
          Telefone_adicional: customerObj.additional_phone || '',
          Observações: customerObj.notes || '',
          Situação_do_cliente: customerObj.status === 1 ? 'Ativo' : (typeof customerObj.status === 'number' ? 'Inativo' : ''),
          Motivo_desativação: customerObj.reason_deactivation || '',
          CEP: address.postal_code || '',
          Logradouro: address.street || '',
          Número: address.number || '',
          Complemento: address.complement || '',
          Bairro: address.neighborhood || '',
          Cidade_Município: address.city || '',
          UF: address.state || '',
          Descrição_endereço: address.description || '',
          Código_banco: payment.bank_code || '',
          Nome_banco: payment.bank_name || '',
          Agência: payment.bank_branch || '',
          Conta_e_dígito: payment.bank_account || '',
          Tipo_recebimento: payment.bank_account_type || '',
          Titular_conta: payment.account_owner || '',
          Chave_Pix: payment.bank_pix || '',
          Número_beneficiário: payment.beneficiary_number || '',
          Valor_benefício_anterior: payment.previous_benefit_amount || '',
          Espécie: payment.category || '',
          DIB: payment.dib || '',
          Descrição_Contrato: contract.name || '',
          Valor_Contrato: contract.value || '',
          Número_Contrato: contract.number || '',
          Forma_desconto_Contrato: contract.contractual_discount_id || '',
          Parceiro_Contrato: contract.business_partner || '',
          Comissão_parceiro_Contrato: contract.partner_commission || '',
          Data_contratação_Contrato: contract.date_of_contract ? moment.utc(contract.date_of_contract).format('DD/MM/YYYY') : '',
          Situação_contratual: contract.status === 1 ? 'Adimplente' : (typeof contract.status === 'number' ? 'Inadimplente' : ''),
        };
        formatedExportData.push(customerFormat);
      });
    }
    setDataExport(formatedExportData);
  }, []);

  const getData = useCallback(async (pageIndexToFetch, pageSizeToFetch) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${routeName}/filter`, {
        name: filterName,
        lastname: filterLastname,
        company_id: filterCompany,
        cpf_cnpj: filterDocId,
        status: filterStatusCustomer,
        status_contract: filterStatusContract,
        partner_contract: filterPartner,
        discount_contract: filterDiscount,
        city_address: filterCity,
        state_address: filterState,
        page: pageIndexToFetch + 1, // pageIndex is 0-based
        limit: pageSizeToFetch,
      });

      if (response.data && Array.isArray(response.data.customers) && typeof response.data.totalItems === 'number') {
        setTableData(response.data.customers);
        setTotalPages(Math.ceil(response.data.totalItems / pageSizeToFetch));
        exportDataConstruct(response.data.customers);
      } else {
        console.warn("Resposta inesperada da API:", response.data);
        setTableData([]);
        setTotalPages(0);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setApiErrors(err);
      setTableData([]);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    filterName, filterLastname, filterCompany, filterDocId, filterStatusCustomer,
    filterStatusContract, filterPartner, filterDiscount, filterCity, filterState,
    routeName, exportDataConstruct
  ]);

  const fetchDataCallback = useCallback(({ pageIndex, pageSize: newPageSize }) => {
    setCurrentPageSize(newPageSize); // Update current page size state
    getData(pageIndex, newPageSize);
  }, [getData]);

  const getFilterOptions = useCallback(async () => {
    try {
      const optionsCompanies = [{ value: '', label: '-- Selecione --' }];
      const getCompanies = await axios.get(`/companies`);
      if (getCompanies && Array.isArray(getCompanies.data)) {
        getCompanies.data.forEach((company) => {
          optionsCompanies.push({ value: company.id, label: company.name });
        });
      }
      filterOptionsCompany.current = optionsCompanies;

      filterOptionsStatusCustomer.current = [
        { value: 1, label: 'Ativo' }, { value: 0, label: 'Inativo' }, { value: '', label: 'Todas' },
      ];
      filterOptionsStatusContract.current = [
        { value: '', label: 'Todas' }, { value: 1, label: 'Adimplente' }, { value: 0, label: 'Inadimplente' },
      ];
    } catch (err) {
      setApiErrors(err);
    } finally {
      setDataLoaded(true); // Ensure this is set after options are loaded
    }
  }, []);

  const getDiscounts = useCallback(async () => {
    if (!filterCompany) {
      setFilterOptionsDiscount([{ value: '', label: '-- Selecione uma empresa --' }]);
      return;
    }
    try {
      const optionsDiscount = [{ value: '', label: '-- Selecione --' }];
      const discounts = await axios.get(`/discounts?company_id=${filterCompany}`);
      if (discounts && Array.isArray(discounts.data)) {
        discounts.data.forEach((discount) => {
          optionsDiscount.push({ value: discount.id, label: discount.name });
        });
      }
      setFilterOptionsDiscount(optionsDiscount);
    } catch (err) {
      setApiErrors(err);
    }
  }, [filterCompany]);

  const handleExportExcel = async () => {
    // abre loading sticky
    const id = notification(
      'Gerando planilha... isso pode levar alguns segundos.',
      'loading',
      0, // ignorado quando sticky
      'Exportação',
      new Date().toLocaleTimeString(),
      { sticky: true }
    );

    try {
      const filters = {
        name: filterName,
        cpf_cnpj: filterDocId,
        email: '',
        status: filterStatusCustomer,
        company_id: filterCompany,
      };

      const response = await axios.post('/customers/export', filters, {
        responseType: 'blob',
        timeout: 600000, // 10 minutos
        signal: AbortController.signal,
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clientes.xlsx');
      document.body.appendChild(link);

      // dá chance da UI renderizar o toast antes do clique
      await new Promise(r => setTimeout(r, 0));
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // atualiza para sucesso e autohide com delay maior
      notification.update(id, {
        message: 'Exportação concluída. O download foi iniciado.',
        type: 'success',
        delay: 100000,  // 30s
        sticky: false,
        title: 'Exportação',
        date: new Date().toLocaleTimeString(),
      });
    } catch (err) {
      console.error('Erro ao exportar:', err);
      notification.update(id, {
        message: 'Erro ao exportar os dados.',
        type: 'error',
        delay: 3000000,
        sticky: false,
        title: 'Exportação',
        date: new Date().toLocaleTimeString(),
      });
    }
  };




  const resetFilter = useCallback(() => {
    setFilterName(''); setFilterLastname(''); setFilterCompany('');
    setFilterCity(''); setFilterState(''); setFilterDocId('');
    setFilterDiscount(''); setFilterStatusCustomer(1); setFilterStatusContract('');
    setFilterPartner('');
  }, []);

  useEffect(() => {
    getFilterOptions();
    getData(0, initialPageSize); // Initial data load
  }, [getFilterOptions, getData, initialPageSize]);

  useEffect(() => {
    if (filterApplied) {
      getData(0, currentPageSize);
      setFilterApplied(false);
    }
  }, [filterApplied, getData, currentPageSize]);

  useEffect(() => {
    getDiscounts();
  }, [filterCompany, getDiscounts]);

  const confirmDelete = useCallback(
    async (id) => {
      setIsLoading(true);
      try {
        await axios.delete(`${routeName}/${id}`);
        notification(`${dataName} removido com sucesso`, 'success');
        getData(0, currentPageSize);
      } catch (err) {
        setApiErrors(err);
        notification(`Erro ao remover ${dataName}.`, 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [notification, dataName, routeName, getData, currentPageSize]
  );

  const memoizedTableColumns = useMemo(() => {
    const columnsWithActions = [...baseTableColumns];
    const statusCustomerColIdx = columnsWithActions.findIndex(col => col.accessor === 'status_customer');

    if (statusCustomerColIdx !== -1) {
      columnsWithActions[statusCustomerColIdx] = {
        ...columnsWithActions[statusCustomerColIdx],
        Header: 'Situação Cliente / Ações',
        Cell: ({ row }) => {
          const dataObject = row.original;
          const linksAction = [
            { onclick: () => navigate(`${routeName}/${dataObject.id}/edit`), faicon: 'faEdit', text: 'Atualizar' },
          ];
          if (userData.isadmin === 1) {
            linksAction.push({
              custom: (
                <ButtonModal
                  onConfirm={() => confirmDelete(dataObject.id)}
                  message={`Confirma remoção do ${dataName} ${dataObject.name}?`}
                  buttonProps={{ text: 'Remover', faicon: 'faTrash' }}
                  textModal={{ title: `Remover ${dataName}`, textConfirm: 'Remover' }}
                  dropdown
                />
              ),
            });
          }
          const statusBadge = (
            <Badge bg={`${dataObject.status === 1 ? 'success' : 'danger'}-light`} text={dataObject.status === 1 ? 'success' : 'danger'}>
              <span className="indicator" />{dataObject.status === 1 ? 'Ativo' : 'Inativo'}
            </Badge>
          );
          return (
            <div className="d-flex justify-content-between align-items-center">
              <span>{statusBadge}</span>
              <CardHeaderMore links={linksAction} className="" />
            </div>
          );
        },
      };
    }

    const statusContractColIdx = columnsWithActions.findIndex(col => col.accessor === 'status_contract');
    if (statusContractColIdx !== -1) {
      columnsWithActions[statusContractColIdx] = {
        ...columnsWithActions[statusContractColIdx],
        Cell: ({ row }) => {
          const contractStatus = row.original.CustomerContracts && row.original.CustomerContracts[0] ? row.original.CustomerContracts[0].status : null;
          if (contractStatus === null || typeof contractStatus === 'undefined') return '';
          return (
            <Badge bg={`${contractStatus === 1 ? 'success' : 'danger'}-light`} text={contractStatus === 1 ? 'success' : 'danger'}>
              <span className="indicator" />{contractStatus === 1 ? 'Adimplente' : 'Inadimplente'}
            </Badge>
          );
        }
      };
    }
    return columnsWithActions;
  }, [baseTableColumns, userData, navigate, dataName, confirmDelete]);

  // Construir a string de filtros aplicados
  const getAppliedFilters = () => {
    const filters = [];

    if (filterName) filters.push(`Nome: ${filterName}`);
    if (filterLastname) filters.push(`Sobrenome: ${filterLastname}`);
    if (filterDocId) filters.push(`CPF/CNPJ: ${filterDocId}`);

    if (filterCompany) {
      const companyOption = filterOptionsCompany.current.find(
        company => Number(company.value) === Number(filterCompany)
      );
      if (companyOption) filters.push(`Empresa: ${companyOption.label}`);
    }

    if (filterCity) filters.push(`Cidade: ${filterCity}`);
    if (filterState) filters.push(`UF: ${filterState}`);

    if (filterDiscount) {
      const discountOption = filterOptionsDiscount.find(
        discount => Number(discount.value) === Number(filterDiscount)
      );
      if (discountOption) filters.push(`Desconto: ${discountOption.label}`);
    }

    if (filterPartner) filters.push(`Parceiro: ${filterPartner}`);

    if (filterStatusCustomer !== '') {
      const statusOption = filterOptionsStatusCustomer.current.find(
        status => Number(status.value) === Number(filterStatusCustomer)
      );
      if (statusOption) filters.push(`Situação Cliente: ${statusOption.label}`);
    }

    if (filterStatusContract !== '') {
      const statusOption = filterOptionsStatusContract.current.find(
        status => Number(status.value) === Number(filterStatusContract)
      );
      if (statusOption) filters.push(`Situação Contratual: ${statusOption.label}`);
    }

    return filters;
  };

  const appliedFilters = getAppliedFilters();

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs title={titleName} />
      <div className="page-header d-flex justify-content-between align-items-center">
        <h1 className="page-heading">{titleName}</h1>
        <div>
          <Link to={`${routeName}/new`}>
            <Button variant="primary" className="text-uppercase">
              <FaPlus /> Cadastrar
            </Button>
          </Link>
        </div>
      </div>
      <section>
        {dataLoaded ? (
          <>
            {appliedFilters.length > 0 && (
              <Row className="mb-1">
                <Col lg={12}>
                  <h6 className="page-heading">
                    {appliedFilters.map((filter, index) => (
                      <React.Fragment key={index}>
                        <b>{index === 0 ? '' : ' | '}{filter}</b>
                      </React.Fragment>
                    ))}
                  </h6>
                </Col>
              </Row>
            )}
            <Card className="card-table mb-5">
              <ErrorProcessor error={apiErrors} />
              <DataTable
                columns={memoizedTableColumns}
                items={tableData}
                defaultPageSize={initialPageSize}
                fetchData={fetchDataCallback}
                pageCountFromServer={totalPages}
                defaultSort="name"
                customActions={
                  <>
                    <ButtonModal
                      onConfirm={() => setFilterApplied(true)}
                      message={
                        <Row sm={12}>
                          <Col sm={12} className="mb-0 d-flex justify-content-end">
                            <Button
                              title="Clique para limpar o filtro"
                              size="sm"
                              variant="info"
                              onClick={resetFilter}
                            >
                              <FaRedoAlt className="text-white" />
                            </Button>
                          </Col>
                          <Col md={6} lg={4} className="mb-2">
                            <Form.Label>Nome:</Form.Label>
                            <Form.Control
                              value={filterName}
                              onChange={(e) => setFilterName(e.target.value)}
                            />
                          </Col>
                          <Col md={6} lg={4} className="mb-2">
                            <Form.Label>Sobrenome:</Form.Label>
                            <Form.Control
                              value={filterLastname}
                              onChange={(e) => setFilterLastname(e.target.value)}
                            />
                          </Col>
                          <Col md={6} lg={4} className="mb-2">
                            <Form.Label>CPF / CNPJ:</Form.Label>
                            <Form.Control
                              value={filterDocId}
                              onChange={(e) => setFilterDocId(e.target.value.replace(/[\s.-]/g, ''))}
                            />
                          </Col>
                          <Col md={6} lg={4} className="mb-2">
                            <Form.Label>Empresa:</Form.Label>
                            <Form.Control
                              as="select"
                              value={filterCompany}
                              onChange={(e) => setFilterCompany(e.target.value)}
                            >
                              {filterOptionsCompany.current.map(opt => (
                                <option key={opt.value || v4()} value={opt.value}>{opt.label}</option>
                              ))}
                            </Form.Control>
                          </Col>
                          <Col md={6} lg={4} className="mb-2">
                            <Form.Label>Cidade (Endereço):</Form.Label>
                            <Form.Control
                              value={filterCity}
                              onChange={(e) => setFilterCity(e.target.value)}
                            />
                          </Col>
                          <Col md={6} lg={4} className="mb-2">
                            <Form.Label>UF (Endereço):</Form.Label>
                            <Form.Control
                              value={filterState}
                              onChange={(e) => setFilterState(e.target.value)}
                            />
                          </Col>
                          <Col md={6} lg={4} className="mb-2">
                            <Form.Label>Desconto Contratual:</Form.Label>
                            <Form.Control
                              as="select"
                              value={filterDiscount}
                              onChange={(e) => setFilterDiscount(e.target.value)}
                              disabled={!filterCompany || filterOptionsDiscount.length <= 1}
                            >
                              {filterOptionsDiscount.map(opt => (
                                <option key={opt.value || v4()} value={opt.value}>{opt.label}</option>
                              ))}
                            </Form.Control>
                          </Col>
                          <Col md={6} lg={4} className="mb-2">
                            <Form.Label>Parceiro Contrato:</Form.Label>
                            <Form.Control
                              value={filterPartner}
                              onChange={(e) => setFilterPartner(e.target.value)}
                            />
                          </Col>
                          <Col md={6} lg={4} className="mb-2">
                            <Form.Label>Situação Cliente:</Form.Label>
                            <Form.Control
                              as="select"
                              value={filterStatusCustomer}
                              onChange={(e) => setFilterStatusCustomer(e.target.value)}
                            >
                              {filterOptionsStatusCustomer.current.map(opt => (
                                <option key={opt.value === '' ? 'all' : opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </Form.Control>
                          </Col>
                          <Col md={6} lg={4} className="mb-2">
                            <Form.Label>Situação Contratual:</Form.Label>
                            <Form.Control
                              as="select"
                              value={filterStatusContract}
                              onChange={(e) => setFilterStatusContract(e.target.value)}
                            >
                              {filterOptionsStatusContract.current.map(opt => (
                                <option key={opt.value === '' ? 'all' : opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </Form.Control>
                          </Col>
                        </Row>
                      }
                      buttonProps={{
                        variant: 'secondary',
                        className: 'shadow text-white me-2',
                        size: 20,
                        style: { height: '35px' },
                      }}
                      textModal={{
                        title: 'Filtrar pesquisa',
                        textConfirm: 'Filtrar',
                      }}
                    >
                      <FaFilter />
                      <b className="ms-2">Abrir Filtro</b>
                    </ButtonModal>
                    {dataExport.length > 0 && (
                      <ButtonModal
                        onConfirm={handleExportExcel}
                        message={
                          <Row sm={12}>
                            <Col sm={12} className="mb-0 d-flex justify-content-end">
                              <Button
                                title="Clique para limpar o filtro"
                                size="sm"
                                variant="info"
                                onClick={resetFilter}
                              >
                                <FaRedoAlt className="text-white" />
                              </Button>
                            </Col>
                            <Col md={6} lg={4} className="mb-2">
                              <Form.Label>Nome:</Form.Label>
                              <Form.Control
                                value={filterName}
                                onChange={(e) => setFilterName(e.target.value)}
                              />
                            </Col>
                            <Col md={6} lg={4} className="mb-2">
                              <Form.Label>Sobrenome:</Form.Label>
                              <Form.Control
                                value={filterLastname}
                                onChange={(e) => setFilterLastname(e.target.value)}
                              />
                            </Col>
                            <Col md={6} lg={4} className="mb-2">
                              <Form.Label>CPF / CNPJ:</Form.Label>
                              <Form.Control
                                value={filterDocId}
                                onChange={(e) => setFilterDocId(e.target.value.replace(/[\s.-]/g, ''))}
                              />
                            </Col>
                            <Col md={6} lg={4} className="mb-2">
                              <Form.Label>Empresa:</Form.Label>
                              <Form.Control
                                as="select"
                                value={filterCompany}
                                onChange={(e) => setFilterCompany(e.target.value)}
                              >
                                {filterOptionsCompany.current.map(opt => (
                                  <option key={opt.value || v4()} value={opt.value}>{opt.label}</option>
                                ))}
                              </Form.Control>
                            </Col>
                            <Col md={6} lg={4} className="mb-2">
                              <Form.Label>Cidade (Endereço):</Form.Label>
                              <Form.Control
                                value={filterCity}
                                onChange={(e) => setFilterCity(e.target.value)}
                              />
                            </Col>
                            <Col md={6} lg={4} className="mb-2">
                              <Form.Label>UF (Endereço):</Form.Label>
                              <Form.Control
                                value={filterState}
                                onChange={(e) => setFilterState(e.target.value)}
                              />
                            </Col>
                            <Col md={6} lg={4} className="mb-2">
                              <Form.Label>Desconto Contratual:</Form.Label>
                              <Form.Control
                                as="select"
                                value={filterDiscount}
                                onChange={(e) => setFilterDiscount(e.target.value)}
                                disabled={!filterCompany || filterOptionsDiscount.length <= 1}
                              >
                                {filterOptionsDiscount.map(opt => (
                                  <option key={opt.value || v4()} value={opt.value}>{opt.label}</option>
                                ))}
                              </Form.Control>
                            </Col>
                            <Col md={6} lg={4} className="mb-2">
                              <Form.Label>Parceiro Contrato:</Form.Label>
                              <Form.Control
                                value={filterPartner}
                                onChange={(e) => setFilterPartner(e.target.value)}
                              />
                            </Col>
                            <Col md={6} lg={4} className="mb-2">
                              <Form.Label>Situação Cliente:</Form.Label>
                              <Form.Control
                                as="select"
                                value={filterStatusCustomer}
                                onChange={(e) => setFilterStatusCustomer(e.target.value)}
                              >
                                {filterOptionsStatusCustomer.current.map(opt => (
                                  <option key={opt.value === '' ? 'all' : opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </Form.Control>
                            </Col>
                            <Col md={6} lg={4} className="mb-2">
                              <Form.Label>Situação Contratual:</Form.Label>
                              <Form.Control
                                as="select"
                                value={filterStatusContract}
                                onChange={(e) => setFilterStatusContract(e.target.value)}
                              >
                                {filterOptionsStatusContract.current.map(opt => (
                                  <option key={opt.value === '' ? 'all' : opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </Form.Control>
                            </Col>
                          </Row>
                        }
                        buttonProps={{
                          variant: 'success',
                          className: 'shadow text-white me-2',
                          size: 20,

                        }}
                        textModal={{
                          title: 'Filtro para planilha',
                          textConfirm: 'Confirmar',
                        }}
                      >
                        <FaFileExcel /> {/* Ícone de Excel mantido */}
                        <b className="ms-2"></b>
                      </ButtonModal>
                    )}
                  </>
                }
              />
            </Card>
          </>
        ) : (
          <p>Carregando opções de filtro...</p>
        )}
      </section>
    </Container>
  );
}
